import React, { useEffect, useState } from 'react';
import {
  ApiButton,
  Box,
  Button,
  CustomDialog,
  ErrorInfo,
  Icon,
  IconName,
  SectionHeader,
  Typography,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { MutationStatus } from 'react-query';
import { joiResolver } from '@hookform/resolvers/joi';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  CandidateProfileFormData,
  CandidateProfileFormDefaultValues,
  CandidateProfileIsDisableInputs,
} from '../../../../models';
import schemaCreateCandidate from '../../schema/createCandidate';
import { useStyles } from './styles';
import CandidateProfilePersonalParticulars from './CandidateProfilePersonalParticulars';
import CandidateProfileContactDetails from './CandidateProfileContactDetails';
import CandidateProfileEmergencyContacts from './CandidateProfileEmergencyContacts';
import { selectFormEditMode, UpdateFormEdit } from '../../../../store/formSlice';
import CandidateProfileChangeRequestDataTable from './CandidateProfileChangeRequestDataTable';
import RouteKey from '../../../../routes/key';
import { useGetUserDetails } from '../../../../api-refactored/user/useGetUserDetails';
import { CreateCandidateFormLocationState } from '../../logic/useCreateCandidateForm';
import { useGetMyInfoConsentForm } from '../../../../api-refactored/user/useGetMyInfoConsentForm';
import { useWOGAA } from '../../../../hooks/wogaa';

const { profile } = RouteKey.child;
const { candidateDetail, requestForAmendment } = RouteKey.profile.child;
const { serverBusy } = RouteKey.error.child;
const { error: errorRoute } = RouteKey.child;

const redirectIfServerBusy = (errorCode: string, history: any) => {
  if (errorCode === '1020') {
    history.push(`/${errorRoute}/${serverBusy}`);
  }
};

interface CandidateProfileFormProps {
  isHideEditButton: boolean;
  defaultEditState: boolean;
  onCallHandleSubmit?: (formData: CandidateProfileFormData) => void;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  formDefaultValuesViewDetails?: CandidateProfileFormDefaultValues;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  isDisableValidationForDisabledInputs?: boolean;
  isHideExtraFormInfo?: boolean;
  ApiButtonStatus?: MutationStatus;
  submitError?: string;
  isCreate?: boolean;
  isRequestProfileAmendmentOpen?: boolean;
  hasPendingAmendmentRequest?: boolean;
  refetchCandidateProfileDetails?: () => void;
}

const CandidateProfileForm = ({
  isHideEditButton,
  defaultEditState,
  onCallHandleSubmit = () => {
    // do nothing
  },
  formDefaultValues = undefined,
  formDefaultValuesViewDetails = undefined,
  isDisableInputs = undefined,
  isDisableValidationForDisabledInputs = false,
  isHideExtraFormInfo = false,
  ApiButtonStatus = 'idle',
  submitError = '',
  isCreate = false,
  isRequestProfileAmendmentOpen,
  hasPendingAmendmentRequest,
  refetchCandidateProfileDetails,
}: CandidateProfileFormProps) => {
  const { classes } = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  const location = useLocation<CreateCandidateFormLocationState>();

  const isRedirectFromMyInfo = location?.state?.redirectFromMyInfo;

  const [
    openExistingProfileAmendmentRequestModal,
    setOpenExistingProfileAmendmentRequestModal,
  ] = useState<boolean>(false);

  const [isClickRequestForAmendmentButton, setIsClickRequestForAmendmentButton] = useState<boolean>(
    false
  );
  const [displaySingpassButton, setDisplaySingpassButton] = useState<boolean>(false);

  const formHook = useForm<CandidateProfileFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(
      schemaCreateCandidate(isDisableValidationForDisabledInputs ? isDisableInputs : {})
    ),
    defaultValues: formDefaultValues,
  });

  const { refetch: refetchSingpassConsentForm } = useGetMyInfoConsentForm({
    onSuccess: (res) => {
      window.location.href = res.presignedUrl;
      if (isClickRequestForAmendmentButton) {
        sessionStorage.setItem(
          'urlToBeRedirectedAfterLoggedIn',
          JSON.stringify({
            from: {
              pathname: `/${profile}/${candidateDetail.route}/${requestForAmendment.route}`,
              search: '?',
              redirectFromProfile: true,
              isClickRequestForAmendmentButton: true,
              oldValues: formDefaultValues,
            },
          })
        );
      } else {
        sessionStorage.setItem(
          'urlToBeRedirectedAfterLoggedIn',
          JSON.stringify({
            from: {
              pathname: `/${profile}/${candidateDetail.route}`,
              search: '?',
              redirectFromProfile: true,
              isClickRequestForAmendmentButton: false,
            },
          })
        );
      }
    },
    onError: (res) => {
      redirectIfServerBusy(res.response?.data?.code?.toString()!, history);
    },
  });

  const { data: userData } = useGetUserDetails();

  const isSingpassUser = userData?.data?.credentialType === 'SINGPASS';

  const editMode = useSelector(selectFormEditMode);
  const isView = !(isCreate || editMode);
// wogaa start for candidate profile edit
  const { transactionStart } = useWOGAA('myexamsseab-6471');

  const dispatch = useDispatch();
  const { handleSubmit, reset } = formHook;
  const updateFormState = () => {
    if (isSingpassUser && !editMode) {
      setDisplaySingpassButton(true);
    } else {
      transactionStart();
      reset();
      dispatch(UpdateFormEdit(!editMode));
      setDisplaySingpassButton(false);
    }
  };

  const handleClickRequestForAmendment = () => {
    if (hasPendingAmendmentRequest) {
      setOpenExistingProfileAmendmentRequestModal(true);
    } else if (isSingpassUser) {
      transactionStart();
      setIsClickRequestForAmendmentButton(true);
      setDisplaySingpassButton(true);
    } else {
      transactionStart();
      history.push(`/${profile}/${candidateDetail.route}/${requestForAmendment.route}`, {
        formData: { formDataDefaultValues: formDefaultValues },
      });
    }
  };

  const handleCloseExistingProfileAmendmentRequestModal = () =>
    setOpenExistingProfileAmendmentRequestModal(false);

  useEffect(
    () => {
      if (formDefaultValues) {
        formHook.reset(formDefaultValues);
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [formDefaultValues]
  );

  useEffect(() => {
    if (isRedirectFromMyInfo) {
      dispatch(UpdateFormEdit(!editMode));
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  return (
    <Box className={classes.mainContainer}>
      <SectionHeader
        title={t('profile->>CreateCandidateForm.ProfileDetails')}
        containerJustifyContent="flex-start"
      >
        {
            !isHideEditButton &&
            !editMode && (
                <Button
                    startIcon={<Icon color="primary" name={IconName.edit} size="S" />}
                    variant="text"
                    color="primary"
                    id="link-editParticulars"
                    onClick={updateFormState}
                >
                  {t('profile->>CreateCandidateForm.Edit')}
                </Button>
            )
        }
      </SectionHeader>

      {isSingpassUser && displaySingpassButton && !editMode && (
        <>
          <Typography>
            {t('profile->>CreateCandidateForm.SingpassProfileAmendmentRequestStatement')}
          </Typography>
          <Button
            className={classes.retrieveMyInfoWithSingpass}
            startIcon={<Icon size="S" name={IconName.retrieveMyInfoWithSingpass} />}
            variant="text"
            color="primary"
            id="button-retrieveMyInfoWithSingpass"
            onClick={refetchSingpassConsentForm as () => void}
          />
        </>
      )}

      <form onSubmit={handleSubmit((formData) => onCallHandleSubmit(formData))}>
        <CandidateProfilePersonalParticulars
          isEditState={isCreate ? true : editMode}
          formHook={formHook}
          isDisableInputs={isDisableInputs}
          isHideExtraFormInfo={isHideExtraFormInfo}
          formDefaultValues={formDefaultValues}
          formDefaultValuesViewDetails={formDefaultValuesViewDetails}
          isRequestProfileAmendmentOpen={isRequestProfileAmendmentOpen}
          handleClickRequestForAmendment={handleClickRequestForAmendment}
        />

        <CandidateProfileContactDetails
          isEditState={isCreate ? true : editMode}
          formHook={formHook}
          isDisableInputs={isDisableInputs}
          isHideExtraFormInfo={isHideExtraFormInfo}
          formDefaultValues={formDefaultValues}
          formDefaultValuesViewDetails={formDefaultValuesViewDetails}
        />

        <CandidateProfileEmergencyContacts
          isEditState={isCreate ? true : editMode}
          formHook={formHook}
          isDisableInputs={isDisableInputs}
          formDefaultValues={formDefaultValues}
          formDefaultValuesViewDetails={formDefaultValuesViewDetails}
          isView={isView}
        />

        {isView && (
          <CandidateProfileChangeRequestDataTable
            refetchCandidateProfileDetails={refetchCandidateProfileDetails as () => void}
          />
        )}

        {!isView && (
          <>
            <Box className={classes.submitButtonContainer}>
              {!isHideEditButton && (
                <Button
                  id="button-candidateProfile-cancel"
                  className={classes.backButton}
                  variant="outlined"
                  onClick={updateFormState}
                >
                  {t('profile->>CreateCandidateForm.BackToMyProfile')}
                </Button>
              )}

              <ApiButton id="button-candidateProfile-submit" status={ApiButtonStatus} type="submit">
                {t('profile->>CreateCandidateForm.SaveChanges')}
              </ApiButton>
            </Box>

            <ErrorInfo label={submitError || ''} />
          </>
        )}
      </form>
      <CustomDialog
        open={openExistingProfileAmendmentRequestModal}
        title={t('profile->>CreateCandidateForm.ProfileAmendmentRequest')}
        message={t('profile->>CreateCandidateForm.ExistingProfileAmendmentRequest')}
        action={
          <ApiButton
            status="idle"
            type="button"
            onClick={handleCloseExistingProfileAmendmentRequestModal}
            id="proceed-button"
          >
            {t('profile->>CreateCandidateForm.Ok')}
          </ApiButton>
        }
      />
    </Box>
  );
};
export default CandidateProfileForm;
